import { create } from "zustand";


/**
 * The one confirmation the app is asking right now, if any.
 *
 * This exists so `lib/dialog.confirm()` can render a real in-app dialog from a
 * plain async function called out of an event handler. The alternative on web
 * was `window.confirm`, which works but shows the browser's own chrome —
 * "localhost:8081 says", OS buttons, no theme, no control over wording order.
 *
 * Mirrors `reflection-store`: a module-level store, an imperative helper for
 * non-React callers, and a host component that renders whatever is pending.
 */
/**
 * ── How many app-owned native Modals are on screen ──────────────────────────
 *
 * Lives here rather than in its own module for one concrete reason: `ask`
 * below is the only consumer, and a sibling module imported extensionless
 * cannot be resolved by node's ESM loader, which broke `web-dialogs.test.ts`
 * the moment it was split out. Keeping it here also means the deferral can be
 * tested for real instead of described by a source scan.
 *
 * Why it exists: iOS will not present a second native modal window over one
 * already presented. The second never appears, and its window eats every touch
 * until the screen unmounts — a frozen app that looks entirely normal. Both
 * `notify()` and `confirm()` render through `ConfirmDialog`, which is a Modal,
 * so any screen raising one from inside its own open Modal froze the app. Four
 * separate screens shipped exactly that, which is the evidence that "close the
 * sheet first" cannot survive as a convention.
 *
 * A counter is enough: nothing needs to know WHICH modal is open, only whether
 * presenting another would stack.
 *
 * Kept honest by `AppModal` — the only caller of `enterModal` — and by
 * `modal-dialog-authority.test.ts`, which stops a new screen reaching past it
 * to React Native's Modal directly.
 */
type Listener = () => void;

let openCount = 0;
const listeners = new Set<Listener>();

/**
 * Register that a modal is now on screen. Returns the leave function, so the
 * caller cannot forget to pair it — `AppModal` returns it straight out of a
 * `useEffect`, which also covers unmounting while still visible.
 *
 * Idempotent on the way out: React can run an effect cleanup more than once in
 * StrictMode, and a double decrement would report "no modals open" while one
 * is still up, which is precisely the wrong answer.
 */
export function enterModal(): () => void {
  openCount += 1;
  let left = false;
  return () => {
    if (left) return;
    left = true;
    openCount -= 1;
    if (openCount === 0) {
      // Copy first: a listener that unsubscribes itself while we iterate would
      // otherwise mutate the set mid-loop.
      for (const listener of [...listeners]) listener();
    }
  };
}

export function anyModalOpen(): boolean {
  return openCount > 0;
}

/** Fires when the LAST open modal closes; never fires while one is still up. */
export function onAllModalsClosed(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tests only — the counter is module state and would leak between cases. */
export function __resetModalPresence(): void {
  openCount = 0;
  listeners.clear();
}

/**
 * ── Where the dialog is allowed to present ──────────────────────────────────
 *
 * `ConfirmDialog` renders a native Modal, and on iOS a Modal presents from the
 * nearest UIViewController above it. The host is mounted at the ROOT, so that
 * is the root view controller — and a route declared
 * `presentation: 'transparentModal'` (today: `dashboard`) is ALREADY a
 * presentation from that same controller. UIKit refuses the second one, so
 * every `confirm()` and `notify()` raised from inside such a route silently
 * never appeared: nine call sites in the dashboard tree, of which the two
 * deletes were merely the ones a person could notice, because they wait on an
 * answer. The `notify()` ones failed by showing nothing at all.
 *
 * Android and web were unaffected — a Modal there is a Dialog and a DOM
 * portal, and both stack — which is why this survived on those platforms.
 *
 * The fix is a host stack rather than nine rewritten call sites. A route that
 * presents itself modally mounts its OWN host inside its tree, and the
 * innermost registered host is the one that renders; the root host stands down
 * while it exists. The dialog then presents from that route's own controller,
 * which is presenting nothing.
 *
 * `dashboard-dialog-host.test.ts` fails if a modal route is added without one.
 */
let nextHostId = 1;
export function claimDialogHostId(): number {
  return nextHostId++;
}

export type ConfirmRequest = {
  /**
   * A `notice` is one-way: a single button, no cancel, and nothing waits on
   * the answer. It is the same sheet because it is the same interruption —
   * "Unable to send Cue" arriving as browser chrome, in the browser's font,
   * over the app, was the whole reason this exists.
   */
  kind: "confirm" | "notice";
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
  /** Settled exactly once, by `resolve` below. */
  settle: (confirmed: boolean) => void;
};

type ConfirmState = {
  /**
   * Registered dialog hosts, outermost first. The LAST one renders — it is the
   * one deepest inside whatever is currently presented, and therefore the one
   * whose view controller can actually present a Modal.
   */
  hosts: number[];
  registerHost: (id: number) => void;
  unregisterHost: (id: number) => void;
  pending: ConfirmRequest | null;
  /**
   * Asked while another app modal was still on screen, and waiting for it to
   * go. See `ask` — this is the structural half of the iOS freeze fix.
   */
  queued: ConfirmRequest | null;
  ask: (request: ConfirmRequest) => void;
  resolve: (confirmed: boolean) => void;
};

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  hosts: [],

  registerHost(id) {
    // Idempotent: StrictMode runs effects twice, and a duplicate would make
    // unregister leave a dead id at the top — a host that renders nothing.
    set((state) =>
      state.hosts.includes(id) ? state : { hosts: [...state.hosts, id] },
    );
  },

  unregisterHost(id) {
    set((state) => ({ hosts: state.hosts.filter((host) => host !== id) }));
  },

  pending: null,
  queued: null,

  ask(request) {
    // Never stack. A confirmation blocks the screen behind it, so a second one
    // can only arrive from code rather than from a tap — and silently
    // replacing the open one would strand its promise forever, leaving the
    // caller awaiting a dialog nobody can see. Refusing is the safe answer:
    // the new caller is told "not confirmed" and does nothing.
    if (get().pending) {
      request.settle(false);
      return;
    }

    // ── The iOS freeze, closed structurally ──────────────────────────────────
    //
    // This dialog renders as a native Modal. iOS will not present one over a
    // Modal that is already up: it never appears, and its window eats every
    // touch until the screen unmounts. Four separate screens shipped that bug
    // by raising a dialog from inside their own open sheet, so "close the
    // sheet first" is now enforced here rather than remembered there.
    //
    // WAIT, do not refuse. Settling `false` immediately would be safe for the
    // screen and wrong for the person: a destructive confirmation would read
    // as "cancelled" without anyone being asked, and a notice would vanish. So
    // the request is held until the last modal closes and then presented
    // normally. The screen stays responsive throughout, which is the property
    // that was actually lost.
    //
    // If the modal never closes the dialog never shows — the caller simply
    // does not proceed. That is a dead end without a freeze, and it is a
    // strictly better failure than the one this replaces.
    if (anyModalOpen()) {
      // `__DEV__` is injected by Metro and does not exist anywhere else, so a
      // bare reference throws the moment this store is loaded outside the app
      // — which is how the tests load it.
      if (typeof __DEV__ !== "undefined" && __DEV__) {
        console.error(
          "[dialog] confirm()/notify() was called while an app modal was open. " +
            "It has been deferred until the modal closes, but the calling " +
            "screen should close its own modal first — see the modal " +
            "presence counter above.",
        );
      }
      // One deep, for the same reason `pending` is one deep.
      if (get().queued) {
        request.settle(false);
        return;
      }
      set({ queued: request });
      const stop = onAllModalsClosed(() => {
        stop();
        const held = get().queued;
        if (!held) return;
        set({ queued: null });
        // Through `ask` again rather than straight to `pending`: by the time
        // the modal closed something else may legitimately be asking.
        get().ask(held);
      });
      return;
    }

    set({ pending: request });
  },

  resolve(confirmed) {
    const { pending } = get();
    if (!pending) return;
    // Cleared before settling. Today this ordering is not load-bearing:
    // `settle` resolves a promise, and its continuations run as microtasks
    // after this function has already returned, so both orders behave
    // identically — verified by swapping them, which changed no test but the
    // one asserting the order itself. It is kept because that stops being true
    // the moment `settle` is ever a plain synchronous callback, and then a
    // caller asking a second question from its own answer would be refused by
    // the guard above for no reason it could see.
    set({ pending: null });
    pending.settle(confirmed);
  },
}));

/** Imperative entry point for `lib/dialog`, which is not a component. */
export function askConfirm(
  request: Omit<ConfirmRequest, "settle" | "kind">,
): Promise<boolean> {
  return new Promise((settle) => {
    useConfirmStore.getState().ask({ ...request, kind: "confirm", settle });
  });
}

/**
 * A one-way message. Fire and forget: nothing awaits it, and if a dialog is
 * already up this one is dropped by the no-stacking rule above — acceptable
 * precisely because the user is, in that case, already looking at something
 * rather than at a tap that appeared to do nothing.
 *
 * If an app MODAL is open rather than a dialog, the notice is deferred instead
 * of dropped, and appears when the modal closes. See `ask`.
 */
export function showNotice(title: string, message: string | undefined, ok: string): void {
  useConfirmStore.getState().ask({
    kind: "notice",
    title,
    message,
    confirmLabel: ok,
    cancelLabel: ok,
    destructive: false,
    settle: () => {},
  });
}
